/**
 * @fileoverview Component for displaying a participants menu item.
 * This component includes a link to a specific route and displays an icon and title.
 */

import { NavLink, useParams } from 'react-router-dom'

import ParticipantsIcon from '../../assets/menu/participants.svg'
import PlusIcon from '../../assets/plus.svg'
import { filters } from '../../config'
import { useModal } from '../../hooks/useModal'
import { useGetParticipantsCountQuery } from '../../services/participants'
import { NewParticipant } from '../modals/content/NewParticipant'
import { MenuItem } from './MenuItem'

/**
 * @function classNames
 * @description Helper function to join class names with spaces.
 * @param {...string[]} classes - The class names to join.
 * @returns {string} - The joined class names.
 */
function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ')
}

const menuItems = [
    {
        title: 'Requiring review',
        count: 0,
        query: filters.participants.review,
        uri: 'review',
    },
    {
        title: 'Lower priority',
        count: 0,
        query: filters.participants.not_qualified,
        uri: 'not_qualified',
    },
    {
        title: 'High priority',
        count: 0,
        query: filters.participants.qualified,
        uri: 'qualified',
    },
    {
        title: 'Excluded',
        count: 0,
        query: filters.participants.excluded,
        uri: 'excluded',
    },
    {
        title: 'Invited',
        count: 0,
        query: filters.participants.invited,
        uri: 'invited',
    },
    {
        title: 'Randomised',
        count: 0,
        query: filters.participants.randomised,
        uri: 'randomised',
    },
]

/**
 * @function ParticipantsMenuItem
 * @description Component for displaying a participants menu item.
 * @returns {JSX.Element} - A React component that renders the participants menu item.
 */
export function ParticipantsMenuItem() {
    const [open] = useModal()
    const { filter } = useParams()

    const sp = new URLSearchParams(location.search)
    sp.delete('offset')
    sp.delete('limit')

    const areParticipantsExpanded = Boolean(location.pathname.match(/\/app\/participants/))

    const { data: review } = useGetParticipantsCountQuery(filters.participants.review)
    const { data: not_qualified } = useGetParticipantsCountQuery(filters.participants.not_qualified)
    const { data: qualified } = useGetParticipantsCountQuery(filters.participants.qualified)
    const { data: excluded } = useGetParticipantsCountQuery(filters.participants.excluded)
    const { data: invited } = useGetParticipantsCountQuery(filters.participants.invited)
    const { data: randomised } = useGetParticipantsCountQuery(filters.participants.randomised)

    const counters = {
        review: review?.count,
        not_qualified: not_qualified?.count,
        qualified: qualified?.count,
        excluded: excluded?.count,
        invited: invited?.count,
        randomised: randomised?.count,
    }

    const activeIdx = menuItems.findIndex((item) => item.uri === filter)
    const offset = Math.max(activeIdx, -40)

    return (
        <>
            <div className="relative flex flex-col">
                <MenuItem
                    isActive={areParticipantsExpanded}
                    icon={ParticipantsIcon}
                    title="Participants"
                    to={`/app/participants/all?${sp.toString()}`}
                />

                {areParticipantsExpanded && (
                    <button
                        onClick={() => open({ content: <NewParticipant /> })}
                        className="absolute w-8 h-8 transition-all bg-indigo-100 hover:bg-indigo-200 flex justify-center items-center rounded-md p-1 right-2 top-2"
                    >
                        <PlusIcon className="w-5 h-5 fill-indigo-500" />
                    </button>
                )}
            </div>

            {areParticipantsExpanded && (
                <div className="flex pl-4 items-start">
                    <div className="w-[2px] h-[calc(100%_-_16px)] mt-2 bg-gray-100 overflow-hidden">
                        <div
                            className="w-[2px] h-6 bg-indigo-500 transition-transform"
                            style={{ transform: `translateY(${offset * 40}px` }}
                        />
                    </div>

                    <div className="pl-8 flex grow">
                        <ul className="w-full text-sm font-medium">
                            {menuItems.map(({ title, uri }) => (
                                <NavLink
                                    key={uri}
                                    to={uri + `?${sp.toString()}`}
                                    className={({ isActive }) =>
                                        classNames(
                                            'hover:text-gray-900',
                                            isActive ? 'text-gray-900' : 'text-gray-600'
                                        )
                                    }
                                >
                                    <li className="py-2.5 flex justify-between items-center">
                                        <span>{title}</span>
                                        <span className="pr-3 font-medium text-xs">
                                            {counters[uri as keyof typeof counters]}
                                        </span>
                                    </li>
                                </NavLink>
                            ))}
                        </ul>
                    </div>
                </div>
            )}
        </>
    )
}
